// The new-video sheet (spec-v3-formats.md, Web E1): first the type picker, grouped (Stories, Marketing, Learning...),
// each type a card with its sample or drawn plate; then the chosen type's brief, asked the way the Director asks it.
// Creating the project opens it. The Director opens this sheet too (show_formats / new_video_sheet directives).
// The ad keeps v2's one-question start ("What are you advertising?").
import { useEffect, useMemo, useState, type FormEvent } from "react";
import { api } from "../api";
import { director } from "../director";
import { draftFor, groupFormats, missingFields, titleFromIdea, type FormatInfo, type VideoBriefDraft } from "../formats";
import { navigate } from "../router";
import { closeNewVideo, get as getStore, toast, useStore } from "../store";
import { collectionsChanged, pickChannel, readCurrentChannel } from "../collections";
import { Icon, Sheet } from "./bits";
import { BriefInput } from "./BriefFields";
import { FormatArt, FormatCover } from "./FormatArt";
import { FormatIcon, useFormats } from "./FormatMark";

/** Mounted once (App): shows whenever the store asks for it. */
export function NewVideoHost() {
  const req = useStore((s) => s.newVideo);
  if (!req) return null;
  return <NewVideoSheet key={req.format ?? "pick"} start={req.format} highlight={req.highlight ?? []} onClose={closeNewVideo} />;
}

export function NewVideoSheet({ start, highlight, onClose }: { start?: string; highlight: string[]; onClose: () => void }) {
  const formats = useFormats();
  const [picked, setPicked] = useState<string | null>(start ?? null);
  const format = formats?.find((f) => f.id === picked) ?? null;
  const only = useStore((s) => s.newVideo?.formats); // v4: a collection's "+ New episode" offers what it takes
  // the sheet's title says where you are
  const title = format ? format.name : "New video";
  return (
    <Sheet title={title} onClose={onClose} className={`nv-sheet${format ? " nv-sheet-brief" : ""}`}>
      {!formats ? (
        <div className="sheet-body">
          <TypeSkeleton />
        </div>
      ) : !formats.length ? (
        <div className="sheet-body">
          <p className="muted">The video types didn't load. Check your connection and open this again.</p>
        </div>
      ) : format ? (
        <BriefStep format={format} onBack={start ? undefined : () => setPicked(null)} onDone={onClose} />
      ) : (
        <TypePicker formats={only?.length ? formats.filter((f) => only.includes(f.id)) : formats} highlight={highlight} onPick={(f) => setPicked(f.id)} />
      )}
    </Sheet>
  );
}

function TypeSkeleton() {
  return (
    <ul className="types" aria-label="Loading video types">
      {[0, 1, 2, 3].map((i) => (
        <li key={i} className="type-skel" />
      ))}
    </ul>
  );
}

export function TypePicker({ formats, highlight, onPick }: { formats: FormatInfo[]; highlight: string[]; onPick: (f: FormatInfo) => void }) {
  const groups = useMemo(() => groupFormats(formats), [formats]);
  const suggested = formats.filter((f) => highlight.includes(f.id));
  return (
    <div className="sheet-body nv-pick">
      <p className="nv-lede">Pick what you're making. Each one asks a few questions, then your Director writes it and makes it with you.</p>
      {!!suggested.length && (
        <section className="tgroup" aria-labelledby="tg-suggested">
          <h3 id="tg-suggested" className="tgroup-title">
            Suggested for you
          </h3>
          <ul className="types">
            {suggested.map((f) => (
              <li key={f.id}>
                <TypeCard f={f} onPick={onPick} suggested />
              </li>
            ))}
          </ul>
        </section>
      )}
      {groups.map((g) => (
        <section key={g.group} className="tgroup" aria-labelledby={`tg-${g.group}`}>
          <h3 id={`tg-${g.group}`} className="tgroup-title">
            {g.group}
          </h3>
          <ul className="types">
            {g.formats.map((f) => (
              <li key={f.id}>
                <TypeCard f={f} onPick={onPick} />
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}

export function TypeCard({ f, onPick, suggested = false }: { f: FormatInfo; onPick: (f: FormatInfo) => void; suggested?: boolean }) {
  const [hot, setHot] = useState(false);
  return (
    <button
      type="button"
      className={`type cao-fmt${suggested ? " is-suggested" : ""}`}
      data-group={f.group.toLowerCase()}
      data-action="newvideo.format" onClick={() => onPick(f)}
      onPointerEnter={() => setHot(true)}
      onPointerLeave={() => setHot(false)}
      onFocus={() => setHot(true)}
      onBlur={() => setHot(false)}
    >
      <span className="type-cover">
        <FormatCover format={f.id} sample={f.sample} active={hot} />
        {f.status === "beta" && <span className="type-beta">Beta</span>}
      </span>
      <span className="type-text">
        <span className="type-name">
          <FormatIcon format={f.id} size={16} />
          {f.name}
        </span>
        <span className="type-tag">{f.tagline}</span>
      </span>
    </button>
  );
}

/** v4 (V4-H2): where a new video goes: the collection and channel it was started from, else the current channel. */
function destination(channels: { id: string }[]): { channelId: string | null; collectionId?: string } {
  const req = getStore().newVideo;
  const channelId = req?.channelId ?? pickChannel(channels, null, readCurrentChannel())?.id ?? null;
  return { channelId, ...(req?.collectionId ? { collectionId: req.collectionId } : {}) };
}

function BriefStep({ format, onBack, onDone }: { format: FormatInfo; onBack?: () => void; onDone: () => void }) {
  if (format.id === "ad") return <AdStart onBack={onBack} onDone={onDone} />;
  return <VideoStart format={format} onBack={onBack} onDone={onDone} />;
}

function StepHead({ format, onBack }: { format: FormatInfo; onBack?: () => void }) {
  return (
    <header className="nv-head">
      {onBack && (
        <button type="button" className="back nv-back" onClick={onBack}>
          <Icon name="back" size={16} /> All video types
        </button>
      )}
      <div className="nv-plate">
        <FormatArt format={format.id} />
      </div>
      <p className="nv-desc">
        {format.description}
        {format.status === "beta" && <span className="tag nv-beta">Beta</span>}
      </p>
      {!!format.examples.length && (
        <p className="nv-examples">
          <span className="muted">For example:</span> “{format.examples[0]}”
        </p>
      )}
    </header>
  );
}

function VideoStart({ format, onBack, onDone }: { format: FormatInfo; onBack?: () => void; onDone: () => void }) {
  const [brief, setBrief] = useState<VideoBriefDraft>(() => draftFor(format));
  const [busy, setBusy] = useState(false);
  // v4 (V4-M1): "I'll make it in Suno": the project opens on the Suno pack (writing it is priced there, not here)
  const [suno, setSuno] = useState(false);
  const channels = useStore((s) => s.me?.channels ?? []);
  useEffect(() => setBrief(draftFor(format)), [format]);
  const missing = missingFields(format, brief);
  const idea = (brief.idea ?? "").trim();
  const ready = !missing.length && idea.length >= 2;
  const noun = format.name.split(" or ")[0]!.toLowerCase();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!ready) return;
    setBusy(true);
    try {
      const clean: VideoBriefDraft = { ...brief, idea, characters: brief.characters?.filter((c) => c.name.trim()) };
      if (!clean.characters?.length) delete clean.characters;
      const p = await api.createProject({ title: brief.title?.trim() || titleFromIdea(idea, format), format: format.id, ...destination(channels), brief: clean });
      if (p && destination(channels).collectionId) collectionsChanged();
      director.context(`The user started a new ${format.name.toLowerCase()} by tapping: "${p.title}" (project ${p.id}, format ${format.id}). It's open on their screen.${suno ? " They chose to make the song themselves in Suno: don't generate music; the Suno pack is open." : ""}`);
      onDone();
      navigate(`/p/${p.id}${suno ? "?suno=1" : ""}`);
    } catch (err) {
      toast(`Couldn't start the ${noun}. ${(err as Error).message}`, "bad");
      setBusy(false);
    }
  };

  return (
    <form className="sheet-body nv-brief" onSubmit={submit}>
      <StepHead format={format} onBack={onBack} />
      {format.briefFields.map((f) => (
        <BriefInput key={f.key} field={f} format={format} brief={brief} onChange={(patch) => setBrief((b) => ({ ...b, ...patch }))} idPrefix="nv" suno={{ on: suno, set: setSuno }} />
      ))}
      <div className="nv-foot">
        <p className="sheet-note">Starting is free. Your Director writes the script next and shows the price before anything is made.</p>
        <button data-action="newvideo.start" type="submit" className="btn btn-primary btn-block" disabled={busy || !ready}>
          {busy ? "Starting" : `Start the ${noun}`}
        </button>
      </div>
    </form>
  );
}

function AdStart({ onBack, onDone }: { onBack?: () => void; onDone: () => void }) {
  const [product, setProduct] = useState("");
  const [busy, setBusy] = useState(false);
  const channels = useStore((s) => s.me?.channels ?? []);
  const formats = useFormats();
  const ad = formats?.find((f) => f.id === "ad");
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const t = product.trim();
    if (!t) return;
    setBusy(true);
    try {
      const to = destination(channels);
      const p = await api.createProject({ title: t.length > 48 ? `${t.slice(0, 46)}…` : t, ...to, brief: { product: t, brand: channels.find((c) => c.id === to.channelId)?.name ?? channels[0]?.name ?? "" } });
      if (to.collectionId) collectionsChanged();
      director.context(`The user started a new ad by tapping: "${p.title}" (project ${p.id}). It's open on their screen.`);
      onDone();
      navigate(`/p/${p.id}`);
    } catch (err) {
      toast(`Couldn't start the ad. ${(err as Error).message}`, "bad");
      setBusy(false);
    }
  };
  return (
    <form className="sheet-body nv-brief" onSubmit={submit}>
      {ad && <StepHead format={ad} onBack={onBack} />}
      <label className="field">
        <span className="field-label">What are you advertising?</span>
        <input value={product} onChange={(e) => setProduct(e.target.value)} placeholder="Our Saturday sourdough bake" required data-autofocus />
        <span className="field-hint">You'll fill in the rest of the brief next, or let your Director ask you.</span>
      </label>
      <button data-action="newvideo.start" type="submit" className="btn btn-primary btn-block" disabled={busy || !product.trim()}>
        {busy ? "Starting" : "Start the ad"}
      </button>
    </form>
  );
}
